import React, { useEffect, useState } from 'react';

import { Status as StatusInterface } from '../../../types';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/chevron-right.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/chevron-left.svg';
import BaseButton from '../../../components/BaseButton';
import { fetchDigitalShelf } from '../helpers';
import { cardTitleStyles } from '../configs';
import ToolTip from '../../../components/Tooltip';

interface Props {
  copyData: any;
  params: any;
  isDigitalShelf?: boolean;
}
interface VariantProps {
  [key: string]: {
    [key: string]: string;
  };
}
interface MarketProps {
  market: string;
  availability: {
    value: number;
    status: string;
  };
  share: {
    value: number;
    status: string;
  };
}

const DigitalShelf = ({ copyData, params, isDigitalShelf }: Props): React.ReactElement => {
  const [status, setStatus] = useState<StatusInterface[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [markets, setMarkets] = useState<MarketProps[] | null>(null);
  const [slicedMarkets, setSlicedMarkets] = useState<MarketProps[] | null>(null);
  const [marketCount, setMarketCount] = useState<number>(0);
  const [countPerPage, setCountPerPage] = useState<number>(0);
  const [gridColsClass, setGridColsClass] = useState<string>('');
  const [fetching, setFetching] = useState<boolean>(true);
  const [gridWidth, setGridWidth] = useState<number>(1000);

  const ITEM_MIN_WIDTH = 150;
  const MIN_COLS = 4;
  const MAX_COLS = 15;

  const doFetch = async (params: any) => {
    try {
      const data = await fetchDigitalShelf(params);
      setMarkets(data.attributes);
      setFetching(false);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
    }
  };

  useEffect(() => {
    if (copyData && params) {
      setFetching(true);
      setMarkets(null);
      setSlicedMarkets(null);
      setCountPerPage(0);
      if (isDigitalShelf) {
        doFetch(params);
      }
    }
  }, [copyData, params]);

  useEffect(() => {
    if (markets) {
      doPageCalc();
    }
  }, [markets, gridWidth]);

  useEffect(() => {
    const handleResize = () => setGridWidth(document.getElementsByClassName('digital-shelf-grid')[0]?.clientWidth);
    window.addEventListener('resize', handleResize);
  });

  useEffect(() => {
    if (markets && markets.length) {
      setTotalPages(Math.ceil(markets.length / countPerPage));
      setGridColsClass(getGridCols(countPerPage));
    }
  }, [countPerPage, gridWidth]);

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
    if (currentPage < totalPages) {
      updatePages();
    }
  }, [currentPage, totalPages, countPerPage]);

  const getGridCols = (count: number) => {
    let gridColsClass = '';
    const colCount = count < MIN_COLS ? count : count > MAX_COLS ? MAX_COLS : count;
    //annoying hack because tailwind can't handle variables in classnames
    switch (colCount) {
      case 4:
        if (gridWidth < 750) {
          gridColsClass = 'grid-cols-4';
        } else if (gridWidth < 950) {
          gridColsClass = 'grid-cols-5';
        } else {
          gridColsClass = 'grid-cols-7';
        }
        break;
      case 5:
        if (gridWidth < 950) {
          gridColsClass = 'grid-cols-5';
        } else {
          gridColsClass = 'grid-cols-7';
        }
        break;
      case 6:
        if (gridWidth < 1000) {
          gridColsClass = 'grid-cols-6';
        } else {
          gridColsClass = 'grid-cols-7';
        }
        break;
      case 7:
        gridColsClass = 'grid-cols-7';
        break;
      case 8:
        gridColsClass = 'grid-cols-8';
        break;
      case 9:
        gridColsClass = 'grid-cols-9';
        break;
      case 10:
        gridColsClass = 'grid-cols-10';
        break;
      case 11:
        gridColsClass = 'grid-cols-11';
        break;
      case 12:
        gridColsClass = 'grid-cols-12';
        break;
      case 13:
        gridColsClass = 'grid-cols-13';
        break;
      case 14:
        gridColsClass = 'grid-cols-14';
        break;
      case 15:
        gridColsClass = 'grid-cols-15';
        break;
      default:
        if (count < MIN_COLS && gridWidth < 600) {
          gridColsClass = 'grid-cols-3';
        } else if (gridWidth < 750 && count < MIN_COLS) {
          gridColsClass = 'grid-cols-4';
        } else if (gridWidth < 900 && count < MIN_COLS) {
          gridColsClass = 'grid-cols-5';
        } else {
          gridColsClass = 'grid-cols-7';
        }
    }

    return gridColsClass;
  };

  const doPageCalc = () => {
    if (markets && markets.length) {
      const gridWidth = document.getElementsByClassName('digital-shelf-grid')[0]?.clientWidth;
      const itemsFit = Math.floor(gridWidth / ITEM_MIN_WIDTH);
      let itemsPerPage = itemsFit;
      if (itemsFit > markets.length) {
        itemsPerPage =
          markets.length < MIN_COLS ? markets.length : markets.length > MAX_COLS ? MAX_COLS : markets.length;
      }
      setCountPerPage(itemsPerPage);
    }
  };

  const updatePages = () => {
    if (markets && markets.length) {
      let mCount;
      if (currentPage + 1 === totalPages) {
        mCount = markets?.length;
      } else {
        mCount = (currentPage + 1) * countPerPage;
      }
      setMarketCount(mCount);
      doSlicing();
    }
  };

  const doSlicing = () => {
    if (markets && markets.length) {
      const start = countPerPage * currentPage;
      const end = start + countPerPage;
      const slicedData = [...markets].slice(start, end);
      setSlicedMarkets(slicedData);
    }
  };

  const variantStyles: VariantProps = {
    status: {
      red: 'bg-[#ED2323]',
      yellow: 'bg-[#F7B74D]',
      green: 'bg-[#009965]'
    }
  };

  const labelStyles = 'text-[11px] font-nunitoLight tracking-[0.6px] text-fontBlack pr-1 uppercase';
  const valueStyles =
    'text-[11px] font-bold font-nunitoLight tracking-[0.6px] text-white uppercase w-full min-w-[100px] max-w-[150px] h-10 text-center pt-[12px] px-[28px] rounded-sm';

  const tooltipData = {
    status: 'PARTIAL',
    message: copyData?.digital_shelf_tooltip
  };

  return (
    <div>
      <div>
        <p className={`${cardTitleStyles} inline mr-2`}>{copyData?.overview_digital_shelf_title}</p>
        <ToolTip data={tooltipData} />
      </div>

      <div className="flex">
        {markets && markets.length && isDigitalShelf ? (
          <React.Fragment>
            <div className="w-[20%] min-w-[134px] pt-[8px]">
              <div className="grid grid-cols-1 grid-rows-3 gap-y-2.5">
                <div className={`row-start-2  ${labelStyles} leading-7`}>{copyData?.digital_shelf_availability}</div>
                <div className={`row-start-3 pt-[8px] ${labelStyles} leading-7`}>{copyData?.digital_shelf_share}</div>
              </div>
            </div>

            <div className="w-full">
              <div
                className={`digital-shelf-grid grid ${gridColsClass} grid-rows-3 grid-flow-col justify-items-center gap-y-2.5 gap-x-4`}
              >
                {slicedMarkets &&
                  slicedMarkets.map((market, i) => {
                    return (
                      <React.Fragment key={market.market + i}>
                        <p className={`${labelStyles}`}>{market.market}</p>
                        <div className={`${valueStyles} ${variantStyles.status[market.availability.status]}`}>
                          {market.availability.value}%
                        </div>
                        <div className={`${valueStyles} ${variantStyles.status[market.share.status]}`}>
                          {market.share.value}%
                        </div>
                      </React.Fragment>
                    );
                  })}
              </div>
              {markets.length > countPerPage && (
                <div className="flex items-center mt-8 mx-auto justify-center">
                  <BaseButton
                    variant={currentPage > 0 ? 'plain' : 'plainDisabled'}
                    onClick={() => {
                      if (currentPage > 0) setCurrentPage(currentPage - 1);
                    }}
                  >
                    <span>
                      <ArrowLeftIcon />
                    </span>
                  </BaseButton>
                  <p className="text-[12px] font-nunitoLight tracking-[0.6px]">
                    {marketCount} {copyData.digital_shelf_of} {markets.length} {copyData.digital_shelf_markets}
                  </p>
                  <BaseButton
                    variant={currentPage < totalPages - 1 ? 'plain' : 'plainDisabled'}
                    onClick={() => {
                      if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
                    }}
                  >
                    <span>
                      <ArrowRightIcon />
                    </span>
                  </BaseButton>
                </div>
              )}
            </div>
          </React.Fragment>
        ) : fetching && isDigitalShelf ? (
          <div className="w-full text-center">
            <p className="font-nunitoRegular text-xs text-fontBlock mt-4">{copyData?.loading}</p>
          </div>
        ) : (
          <div className="w-full text-center">
            <p className="text-filterList text-base mt-4">
              {isDigitalShelf ? (
                copyData?.no_data
              ) : (
                <>
                  Interested in monitoring the drivers of digital shelf performance? Edge by Ascential measurement
                  solutions can help. &nbsp;
                  <a
                    className="underline text-blue-500"
                    href={'https://www.ascentialedge.com/demo-request3'}
                    target="_blank"
                  >
                    {copyData?.talk_to_expert}
                  </a>
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalShelf;
